import React, { memo, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import SubpageIcon from '~Assets/icons/more_horiz.svg';
import OfferIcon from '~Assets/icons/local_offer.svg';

import { POSButton, useBongLayout } from '~Providers/BongLayout';
import { CompleteBongQuery_venue_company_categories_items as Item } from '~Scenes/POS/Queries/__queries__';
import { useBridgeApi, useButton, useCurrency, useDispatch, useI18n, useLogger } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import NumberInput from '~ComponentsImproved/Numpad';
import { useInventoryUniverse, useInventoryHelpers } from '~Components/InventoryUniverse';
import useOffers from '~Hooks/useOffers';
import * as actions from '~actions';
import { useCompetition } from '~Providers/CompetitionsProvider';
import Modal from '~ComponentsImproved/Modal';
import MessageInput from '~ComponentsImproved/MessageInput';
import FitTitle from './FitTitle';
import { OFFER_TYPE } from '../../../../../__queries__/globalTypes';
import { useStorage } from '~Components/Storage';
import { useEntryFinder } from '~Components/EntryFinder';
import { getTextColor } from '~Utils/textColor';

const Wrapper = styled.div<{
  posX: number | null;
  posY: number | null;
  color: string;
  touching: boolean;
  disabled: boolean;
}>`
  background: ${({ color }) => color};
  border-radius: 4px;
  text-align: center;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  box-sizing: border-box;
  text-align: center;
  overflow: hidden;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  position: relative;

  outline: ${({ touching }) => (touching ? '4px solid rgba(255,255,255,0.6)' : 'none')};
  z-index: ${({ touching }) => (touching ? 3 : 0)};

  transform: ${({ touching }) => (touching ? 'scale(1.05)' : 'none')};

  grid-row: ${({ posY }) => posY};
  grid-column: ${({ posX }) => posX};

  transition: all 0.1s ease-out;
`;

const Title = styled.div`
  color: #333;
  line-height: var(--lineHeightLarge);
  letter-spacing: var(--letterSpacing);
  font-weight: 600;
  max-width: 80px;
  text-align: center;
`;

const Count = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  font-size: 10px;
  line-height: 1;
  color: var(--colorBlueGray1300);
`;

const InventoryCount = styled.div`
  position: absolute;
  bottom: 4px;
  right: 4px;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 10px;
  line-height: 1;
  background-color: var(--colorBlueGray1300);
  color: var(--colorBlueGray100);
`;

const OutOfStockBadge = styled.div`
  position: absolute;
  bottom: 4px;
  left: 4px;
  right: 4px;
  box-sizing: border-box;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  line-height: 1.1;
  text-align: center;
  background-color: var(--colorBlueGray1300);
  color: var(--colorBlueGray100);
`;

const Trophy = styled.div`
  position: absolute;
  top: 10px;
  left: 1px;
  font-size: 14px;

  .SVG-icon {
    position: relative;
    left: 0;
    bottom: 0;
  }
`;

const Price = styled.div`
  font-size: 11px;
  color: var(--colorBlueGray1300);
  line-height: 1.2;
  margin-top: 2px;
`;

interface Props {
  button: POSButton;
  onPress: (item: Item, manualPrice?: number, volume?: number, manualName?: string) => void;
  onPageClicked: (page: { id: string; name: string }) => void;
  count: number;
  setSubPage?: (subPage: any) => void;
}

const ItemButton: React.FC<Props> = ({ button, onPress, onPageClicked, count, setSubPage }) => {
  const dispatch = useDispatch();
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [showCustomNameInput, setShowCustomNameInput] = useState(false);
  const [customName, setCustomName] = useState('');
  const [volumeInput, setVolumeInput] = useState<string>();
  const { i18n } = useI18n();
  const inventory = useInventoryUniverse();
  const { itemIsOutOfStock } = useInventoryHelpers();
  const { offers } = useOffers();
  const logger = useLogger('item-button');
  const competitions = useCompetition();
  const layout = useBongLayout();
  const bridgeApi = useBridgeApi();
  const currency = useCurrency();
  const [storage] = useStorage();
  const findEntryFromItem = useEntryFinder();

  const inventoryStatus = useMemo(() => {
    const item = inventory.items.find((i) => i.itemId === button.nodeId);

    // If we found an inventory event for the item or/and category
    // compose an object of the inventory status
    if (item) {
      return {
        count: item.count,
        outOfStock: item.outOfStock,
      };
    }

    return {
      count: null,
      outOfStock: false,
    };
  }, [inventory, count]);

  const isPartOfCompetition = useMemo(() => {
    if (!button.nodeId) return false;
    const category = layout.itemToCategoryMap[button.nodeId];

    if (competitions.specificSales.items.includes(button.nodeId)) {
      return true;
    } else if (competitions.specificSales.categories.includes(category?.id ?? '')) {
      return true;
    } else {
      return false;
    }
  }, [button.nodeId, layout.itemToCategoryMap, competitions.specificSales]);

  const { nodeId, color, label, type } = button;

  const item = layout.itemIdMap[nodeId];

  const isStorybook = !!process.env.STORYBOOK_MODE;

  const isServiceChargeItem = i18n(item?.titleLang)
    .toLowerCase()
    .includes('service charge');

  // True when the product's category is out of stock. Also covers layouts.
  const categoryOutOfStock = useMemo(() => {
    if (!button.nodeId) {
      return false;
    }

    const categoryId = layout.itemToCategoryMap[button.nodeId]?.id;
    if (!categoryId) {
      return false;
    }

    return itemIsOutOfStock(categoryId);
  }, [button.nodeId, layout.itemToCategoryMap, itemIsOutOfStock]);

  const isOutOfStock =
    inventoryStatus.outOfStock ||
    categoryOutOfStock ||
    Boolean(inventoryStatus.count && count >= inventoryStatus.count) ||
    inventoryStatus.count === 0;

  const disableButton = (isServiceChargeItem && count >= 1) || isOutOfStock;

  function handleClickEnd() {
    if (type === 'OFFER') {
      const foundOffer = offers.find((o) => o.id === nodeId);

      if (!foundOffer) {
        dispatch(actions.addToast(i18n('TOAST.Offer.NotFound'), true, nodeId, true));
        logger.warn('Offer not found', {
          id: nodeId,
          name: label,
        });
        return;
      }

      // Handle staged bundle offers
      if (
        foundOffer.type === OFFER_TYPE.STAGED_BUNDLE ||
        foundOffer.type === OFFER_TYPE.STAGED_INDIVIDUAL
      ) {
        // Validate that the offer has stages
        if (!foundOffer.stages || foundOffer.stages.length === 0) {
          logger.warn('Staged bundle offer has no stages', { offerId: foundOffer.id });
          return;
        }

        logger.info('Opening staged bundle page', {
          offerId: foundOffer.id,
          offerName: foundOffer.name,
          stageCount: foundOffer.stages.length,
        });

        // Create a special subpage object for the staged bundle
        // This will be detected by Ordering component and rendered with StagedBundleGrid
        if (setSubPage) {
          setSubPage({
            __stagedBundle: true,
            offerName: foundOffer.name || label,
            stages: foundOffer.stages,
          });
        }

        return;
      }

      // Handle regular offers
      foundOffer.items.forEach((i) => {
        const foundItem = layout.itemIdMap[i.id];
        onPress(foundItem);
      });

      return;
    }

    if (!item) {
      onPageClicked({
        id: nodeId,
        name: label,
      });
      return;
    }

    bridgeApi.vibrate('selection');

    if (item.manualName) {
      setShowCustomNameInput(true);

      // If item is NOT volume or manual price we can just return
      if (!item.volumeSuffix && !item.manualPrice) {
        return;
      }
    }

    if (item.volumeSuffix) {
      setVolumeInput(item.volumeSuffix);
      return;
    }

    if (item.manualPrice) {
      setShowPriceInput(true);
      return;
    }

    onPress(item);
  }

  function addItemWithVolume(volume: number) {
    onPress(item, undefined, volume, customName);
    setCustomName('');
  }

  function addItemWithManualPrice(manualPrice: number) {
    if (customName) {
      onPress(item, manualPrice, undefined, customName);
      setShowCustomNameInput(false);
      setCustomName('');
    } else {
      onPress(item, manualPrice);
    }
  }

  function addItemWithManualName() {
    onPress(item, undefined, undefined, customName);
    setShowCustomNameInput(false);
    setCustomName('');
  }

  const inventoryCount = inventoryStatus.count || 0;

  const buttonFunction = useButton({
    onClick: handleClickEnd,
  });

  // Format price for display if showing prices is enabled
  const displayPrice = useMemo(() => {
    if (storage.showPricesOnButtons && button.nodeId) {
      const foundEntry = findEntryFromItem(button.nodeId);
      if (foundEntry?.price) {
        return currency.format(foundEntry.price);
      }
    }
    return null;
  }, [storage.showPricesOnButtons, button.nodeId, findEntryFromItem, currency]);

  return (
    <>
      <Wrapper
        color={color}
        posX={button.x}
        posY={button.y}
        {...buttonFunction}
        disabled={disableButton}>
        <Title>
          <FitTitle color={getTextColor(color)} text={label} />
        </Title>

        {/* Show price if enabled - styled like big POS */}
        {displayPrice && <Price>({displayPrice})</Price>}

        {button.type === 'OFFER' && (
          <Icon src={OfferIcon} height="16px" width="16px" color={'rgba(0, 0, 0, 0.2)'} />
        )}
        {button.type === 'PAGE' && <Icon src={SubpageIcon} color={'rgba(0, 0, 0, 0.2)'} />}
        {count > 0 && <Count>{count}</Count>}
        {isOutOfStock ? (
          // Distinguish a category-wide stockout from an item-level one
          <OutOfStockBadge>
            {i18n(categoryOutOfStock ? 'POS.CategoryOutOfStock' : 'POS.OutOfStock')}
          </OutOfStockBadge>
        ) : (
          inventoryCount > 0 && <InventoryCount>{inventoryStatus.count}</InventoryCount>
        )}
        {isPartOfCompetition && <Trophy>✨</Trophy>}
      </Wrapper>

      {showCustomNameInput && (
        <Modal
          isShowing={true}
          backdropClose={true}
          hide={() => {
            setCustomName('');
            setShowPriceInput(false);
            setShowCustomNameInput(false);
          }}>
          <MessageInput
            autoFocus={true}
            placeHolder={'Enter custom name'}
            value={customName}
            onSubmit={() => {
              // If the item also has volume input or manual price input we just close the custom name modal which will trigger the next input
              if (showPriceInput || volumeInput) {
                setShowCustomNameInput(false);
              } else {
                addItemWithManualName();
              }
            }}
            onInput={(value: string) => setCustomName(value)}
            width={undefined}
          />
        </Modal>
      )}

      {/* To not show inputs at the same time we only want to show the price input after a potential custom name input */}
      {showPriceInput && !showCustomNameInput && (
        <NumberInput
          decimal
          title={i18n('POS.EnterPrice')}
          onClose={() => {
            setShowPriceInput(false);
            setCustomName('');
          }}
          onSubmit={(val) => {
            setShowPriceInput(false);
            addItemWithManualPrice(Number(val) * 100);
          }}
        />
      )}

      {/* To not show inputs at the same time we only want to show the volume input after a potential custom name input */}
      {volumeInput && !showCustomNameInput && (
        <NumberInput
          decimal
          title={`${i18n('POS.HowMany')} ${volumeInput}?`}
          onClose={() => {
            setVolumeInput(undefined);
            setCustomName('');
          }}
          onSubmit={(val) => {
            setVolumeInput(undefined);
            addItemWithVolume(Number(val));
          }}
        />
      )}
    </>
  );
};

export default memo(ItemButton, (prevProps, nextProps) => {
  return (
    prevProps.count === nextProps.count &&
    prevProps.button.id === nextProps.button.id &&
    prevProps.button.label === nextProps.button.label &&
    prevProps.button.color === nextProps.button.color &&
    prevProps.button.x === nextProps.button.x &&
    prevProps.button.y === nextProps.button.y
  );
});
